import { HTMLAttributes } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import NextImage from 'next/image'

import Container from '@/components/base/Container'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type CategoriesGridItem = {
  image: { url: string; alt?: string }
  title: string
  link?: string
}

export type CategoriesGridProps = HTMLAttributes<HTMLElement> &
  Partial<{
    items: CategoriesGridItem[]
    theme: WPPageData['acf']['theme']
  }>

const CategoriesGrid = ({
  items = [],
  theme = 'light',
  className,
  ...props
}: CategoriesGridProps) => {
  const [emblaRef] = useEmblaCarousel({ align: 'start', dragFree: true })

  if (!items.length) return null

  return (
    <section
      {...props}
      className={cn(
        [
          {
            'bg-background text-foreground': theme === 'light',
            'bg-primary text-white': theme === 'dark'
          }
        ],
        'py-[4rem]',
        className
      )}
    >
      <Container>
        <div ref={emblaRef} className="select-none overflow-hidden">
          <div
            className={cn(
              '-ml-gutter flex touch-pan-y',
              // matches the Grid's column-2 start offset, but as scrollable
              // content (track padding) so it scrolls away instead of pinning
              // the viewport, and without affecting any item's own box/ratio
              'lg:pl-[calc((100vw_-_26rem)/12_+_2rem)]'
            )}
          >
            {items.map((item, index) => {
              const Wrapper = item.link ? 'a' : 'div'

              return (
                <div
                  key={`${item.title}-${index}`}
                  className="min-w-0 shrink-0 grow-0 basis-[70%] pl-gutter md:basis-[35%] lg:basis-[23%]"
                >
                  <Wrapper
                    {...(item.link ? { href: item.link } : {})}
                    className="flex flex-col items-start gap-[1.6rem]"
                  >
                    <div className="relative aspect-[3/4] w-full bg-black">
                      <NextImage
                        src={item.image.url}
                        alt={item.image.alt || item.title}
                        fill
                        draggable={false}
                        className="object-cover"
                        sizes="(min-width: 1024px) 25vw, 60vw"
                      />
                    </div>
                    <span className="flex items-center gap-[0.8rem] font-condensed text-label uppercase">
                      {item.title}
                    </span>
                  </Wrapper>
                </div>
              )
            })}
          </div>
        </div>
      </Container>
    </section>
  )
}

export default CategoriesGrid
